/* index.js — UI for /text-to-handwriting.
 *
 * Three lanes share one engine: a sheet from typed text, a PDF filled in by
 * hand, and the visitor's own handwriting captured on the spot. Everything the
 * page does is deterministic from a seed, so the preview on screen and the file
 * that lands in Downloads are the same page — which is not a given: layout and
 * export are separate passes in most tools of this kind, and the visitor finds
 * out at the end.
 *
 * Two things here are load-bearing and easy to undo by accident:
 *
 *   • a knob that only changes how a glyph is INKED (slant, unsteadiness,
 *     pressure, colour) must not trigger a re-layout. Measured: layout costs
 *     17 ms for a page and 59 ms for four, the draw costs ~40 ms — so baking
 *     those into the layout is the difference between a slider that answers
 *     while you drag and one that answers when you let go;
 *   • settings apply to the SELECTION when there is one. Everything below
 *     routes through applyChange(), never straight into S.
 */

import { HANDS, FACES, handsFor, handById, loadFace, registerCustomFace, unsupportedScripts, scriptsIn } from './fonts.js';
import { layoutDocument, layoutRun, PAGES, xHeightOf, DEFAULT_MARGINS } from './layout.js';
import { renderPage, drawRun, PAPERS, PENS } from './render.js';
import { rulingOf } from './paper.js';
import { pngPage, pdfDocument, deliver } from './export.js';
import { hashStr } from './warp.js';
import * as PdfFill from './pdf-fill.js';
import * as Own from './own-hand.js';
import { docText } from '../document-text.js';
import { pageKeys, parsePages, formatPages, fitToday } from './pages.js';

const CFG = window.__HW || { slug: 'text-to-handwriting', i18n: {} };
const T = CFG.i18n || {};
const $ = (id) => document.getElementById(id);
const t = (k, d) => (T[k] == null ? (d == null ? k : d) : T[k]);

const MARGIN_SETS = {
    normal: DEFAULT_MARGINS,
    narrow: { top: 14, right: 10, bottom: 14, left: 16 },
    wide:   { top: 30, right: 26, bottom: 30, left: 34 },
};

/* Which settings can be honoured by redrawing, and which move letters. */
const INK_ONLY = ['slant', 'wobble', 'press', 'pen'];
const LAYOUT_KEYS = ['handId', 'sizeMm', 'jitter', 'tight'];

const S = {
    handId: 'caveat',
    sizeMm: 3.2,
    slant: 0,
    wobble: 1,
    press: 1,
    jitter: 1,
    tight: 0,
    lineHeight: 2.45,
    paper: 'lined',
    pen: 'blue_ball',
    page: 'a4',
    landscape: false,
    margins: 'normal',
    followRuling: true,
    sizeTouched: false,
    scan: false,
    alphaPreview: false,
    seed: 1,
    pageIndex: 0,
    runs: [],                 // [{ start, end, ...overrides }]
};

let doc = null;              // current layout
let face = null;             // face for the document's own hand
let customFace = null;       // the visitor's own hand, once captured
let resolved = [];           // runs with faces loaded, handed to layout/render
let lastSel = null;          // { start, end } — the selection settings apply to
/* The text as the styled ranges last saw it: the next edit is found by comparing
   with it. Module-wide, not boot()'s own: whoever sets the text (a file import, a
   restored work) must set this too, or the first keystroke after it remaps every
   range by the whole difference. */
let taPrev = '';

/* ── settings that survive a reload ──────────────────────────────── */
function bindSetting(el, key, fallback) {
    if (!el || !window.__toolSettings) return;
    window.__toolSettings.bind(CFG.slug, el, key, fallback);
}

/* ═══════════════════ tabs ═══════════════════ */
const TABS = [
    ['hwTabText', 'hwPanelText'],
    ['hwTabPdf', 'hwPanelPdf'],
    ['hwTabHand', 'hwPanelHand'],
];

function showTab(which) {
    TABS.forEach(([tabId, panelId], i) => {
        const on = i === which;
        const tab = $(tabId), panel = $(panelId);
        if (!tab || !panel) return;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        panel.hidden = !on;
    });
    try { window.__stage && window.__stage(['text', 'pdf', 'own_hand'][which]); } catch (e) {}
    if (which === 2) drawOwnPreview();
}

/* ═══════════════════ style, global and per selection ═══════════════════ */

/* A hand built from a base preset plus a set of overrides. Used for the whole
   document and, separately, for every styled run. */
function handFrom(src) {
    const base = handById(src.handId) || HANDS[0];
    return Object.assign({}, base, {
        slant: (base.slant || 0) + (src.slant || 0),
        wobble: Math.max(0, base.wobble * (src.wobble == null ? 1 : src.wobble)),
        press: Math.max(0.05, Math.min(1.8, base.press * (src.press == null ? 1 : src.press))),
        jitter: Math.max(0, base.jitter * (src.jitter == null ? 1 : src.jitter)),
        tight: (base.tight || 0) + (src.tight || 0),
    });
}

function currentHand() { return handFrom(S); }

function textValue() { return ($('hwText') || {}).value || ''; }

/* Merge a patch into the runs covering [a,b). Overlapping runs are split so the
   list stays sorted and non-overlapping — a range list that can overlap needs a
   resolution rule at every read, and gets one wrong eventually. */
function styleRange(a, b, patch) {
    if (b <= a) return;
    const next = [];
    let inherited = null;
    for (const r of S.runs) {
        if (r.end <= a || r.start >= b) { next.push(r); continue; }
        if (!inherited) inherited = r;                       // first overlap donates its style
        if (r.start < a) next.push(Object.assign({}, r, { end: a }));
        if (r.end > b) next.push(Object.assign({}, r, { start: b }));
    }
    const merged = Object.assign({}, inherited || {}, patch, { start: a, end: b });
    next.push(merged);
    next.sort((x, y) => x.start - y.start);
    S.runs = next.filter(r => r.end > r.start);
}

function runSig() { return S.runs.map(r => r.start + ':' + r.end).join('|'); }

/* The one door every control goes through. With a selection the change lands on
   that range; without one it lands on the document.
 *
 * ⚠️ Three cases, and the middle one cost an hour: an ink-only change is a
 * redraw — UNLESS it created a new run, because which glyph belongs to which run
 * is decided during LAYOUT (`it.run`). Skipping the layout there produced a
 * styled fragment that existed in the list, in the chip and in the state, and
 * was invisible on the sheet. Caught by exporting and counting red pixels: 0.
 */
function applyChange(patch) {
    const inkOnly = Object.keys(patch).every(k => INK_ONLY.includes(k));
    let boundariesMoved = false;
    if (lastSel && lastSel.end > lastSel.start) {
        const before = runSig();
        styleRange(lastSel.start, lastSel.end, patch);
        boundariesMoved = runSig() !== before;
    } else {
        Object.assign(S, patch);
    }
    paintRunList();
    // styling a piece of the text is work on it; a document-wide setting alone
    // does not start one (it is also remembered as a preference)
    draftTouch(!!(lastSel && lastSel.end > lastSel.start));
    if (!inkOnly || boundariesMoved) { schedule(true); return; }
    /* Ink on runs that already exist: the placements stand, only the inking
       changes — but `resolved` still holds the OLD hands, so rebuild it. */
    rebuildResolved();
    schedule(false);
}

/* Text edits move the ranges. Without this a run stays on the character
   POSITIONS it was created at, and a paste at the top silently re-styles a
   different word. */
function remapRuns(pos, removed, added) {
    const d = added - removed;
    if (!d && !removed) return;
    const out = [];
    for (const r of S.runs) {
        let a = r.start, b = r.end;
        if (a >= pos + removed) a += d; else if (a > pos) a = pos;
        if (b >= pos + removed) b += d; else if (b > pos) b = pos;
        if (b > a) out.push(Object.assign({}, r, { start: a, end: b }));
    }
    S.runs = out;
}

/* Every face the document and its styled runs need, loaded before the layout
   runs — layout.js is synchronous on purpose, so an un-awaited face here would
   silently fall back to the wrong one. */
const loadedFaces = new Map();
const faceIdOf = (handId) => (handById(handId) || {}).face;

async function ensureFaces() {
    const want = new Set([faceIdOf(S.handId) || 'caveat']);
    for (const r of S.runs) if (r.handId) want.add(faceIdOf(r.handId));
    for (const fid of want) {
        if (!fid || loadedFaces.has(fid)) continue;
        loadedFaces.set(fid, fid === '__custom' ? customFace : await loadFace(fid));
    }
    if (customFace) loadedFaces.set('__custom', customFace);
    face = loadedFaces.get(faceIdOf(S.handId) || 'caveat') || face;
    rebuildResolved();
    return face;
}

/* Synchronous — every face a run needs is already in loadedFaces by the time a
   run can exist, because creating one goes through a layout pass. */
function rebuildResolved() {
    resolved = S.runs.map(r => ({
        start: r.start, end: r.end,
        font: loadedFaces.get(faceIdOf(r.handId || S.handId)) || face,
        hand: handFrom(Object.assign({ handId: S.handId }, S, r)),
        pen: r.pen || S.pen,
        sizeMm: r.sizeMm,
    }));
}

function layoutOpts() {
    const ruling = rulingOf(S.paper, S.followRuling);
    return {
        page: S.page,
        landscape: S.landscape,
        margins: MARGIN_SETS[S.margins] || DEFAULT_MARGINS,
        sizeMm: S.sizeMm,
        sizeAuto: !S.sizeTouched,
        lineHeight: S.lineHeight,
        seed: S.seed,
        ruling,
        followRuling: !!ruling,
        runs: resolved,
    };
}

/* ── which pages, and what they cost ─────────────────────────────────
   The unit is a PAGE of handwriting (pages.js: why, and what a page key is). The
   «Pages» field picks them; the primitive bills the keys it has not seen today. */
/* How many pages ONE file may carry — for everyone, Premium too: it is about the
   tab surviving, not about the daily cap. Measured 29.10.2026 in Chrome: a
   20-page vector PDF +0.71 GB of the whole browser, 98 pages +2.35 GB and 45 s —
   about 21 MB a page on top of ~0.3 GB (pdf-lib keeps every operator of the file
   in memory). Ten pages per GB of the device's budget, 20…100. */
function maxFilePages() {
    let gb = 4;
    try { if (window.__memBudget) gb = window.__memBudget.gb() || 4; } catch (e) {}
    return Math.max(20, Math.min(100, Math.floor(gb * 10)));
}

function chosenPages(quiet) {
    if (!doc) return null;
    const f = $('hwPages');
    const r = parsePages((f || {}).value, doc.pages.length);
    if (r.error) {
        if (!quiet) {
            window.__toast && window.__toast(fmt(t('pages_bad', 'Which pages? Write them like 1-3 or 2, 5. This document has :n.'), { n: doc.pages.length }), 'warning');
            if (f) f.focus({ preventScroll: true });
        }
        return null;
    }
    const max = maxFilePages();
    // Today's allowance smaller than a file? Then today's fit (beginPages) is the
    // only cut that matters — one proposal, not two in a row.
    let left = Infinity;
    try { if (window.__toolLimit) left = window.__toolLimit.remaining(CFG.slug); } catch (e) {}
    if (!quiet && r.indices.length > max && left >= max) {
        const part = r.indices.slice(0, max);
        if (f) { f.value = formatPages(part); f.focus({ preventScroll: true }); }
        paintPager();
        paintPagesCost();
        window.__toast && window.__toast(fmt(t('pages_file_cap', 'One file holds up to :max pages — more may not fit in the browser. The «Pages» field now says :pages; take the rest as a next file.'),
            { max, pages: formatPages(part) }), 'info');
        ae('pages_file_cap', { want: r.indices.length, max, total: doc.pages.length });
        return null;
    }
    return r.indices;
}

/* Tell the counter what the next press would bill, so «0 left» over pages already
   taken today says that taking them again is free. A wall this page raised comes
   down once the choice fits what is left: the wall dims the buttons, and a choice
   that fits — proposed by beginPages or typed — must be pressable. */
let walled = false;
function paintPagesCost() {
    const L = window.__toolLimit;
    if (!L || !L.pending || !doc) return;
    const sel = chosenPages(true);
    try {
        const keys = sel ? pageKeys(doc, sel, hashStr) : null;
        L.pending(CFG.slug, keys);
        if (walled && keys && L.freshKeys(CFG.slug, keys).length <= L.remaining(CFG.slug)) {
            walled = false;
            L.clearNote(CFG.slug);
        }
    } catch (e) {}
}

/* Start a run for these pages. Refused with some of today left: put what fits
   into the field — pages already paid plus the first new ones — and say so, so
   the next press takes them and a long text can be taken over several days. */
function beginPages(sel) {
    const L = window.__toolLimit;
    if (!L) return null;
    const keys = pageKeys(doc, sel, hashStr);
    const run = L.begin(CFG.slug, keys);
    if (run) return run;
    walled = true;
    const left = L.remaining(CFG.slug);
    if (left > 0 && left !== Infinity) {
        const fit = fitToday(sel, keys, new Set(L.freshKeys(CFG.slug, keys)), left);
        if (fit.length && fit.length < sel.length) {
            const f = $('hwPages');
            if (f) { f.value = formatPages(fit); f.focus({ preventScroll: true }); }
            paintPagesCost();
            window.__toast && window.__toast(fmt(t('pages_fit', 'Today :left more page(s) can be taken — :pages is in the «Pages» field now. Press the download button again.'),
                { left, pages: formatPages(fit) }), 'info');
            ae('limit_pages_fit', { want: sel.length, left, fit: fit.length, total: doc.pages.length });
        }
    }
    return null;
}

/* ── the frame loop ──────────────────────────────────────────────────
   One redraw per frame at most, and a re-layout only when something actually
   moved. A debounce would be simpler and is what this used to have; it is also
   why the sliders answered on release instead of under the finger. */
let rafId = 0, needLayout = false, working = false;
function schedule(layout) {
    needLayout = needLayout || !!layout;
    if (rafId) return;
    rafId = requestAnimationFrame(async () => {
        rafId = 0;
        if (working) { schedule(false); return; }
        working = true;
        const wantLayout = needLayout; needLayout = false;
        try {
            if (wantLayout || !doc) {
                await ensureFaces();
                doc = layoutDocument(textValue(), face, currentHand(), layoutOpts());
                if (S.pageIndex >= doc.pages.length) S.pageIndex = doc.pages.length - 1;
                if (S.pageIndex < 0) S.pageIndex = 0;
                syncSizeReadout();
                paintPager();
                paintCount();
                paintPagesCost();
            }
            paintPreview();
        } catch (err) {
            window.__handleError && window.__handleError(err, CFG.slug);
        } finally { working = false; }
    });
}

function renderOpts(extra) {
    return Object.assign({
        paper: S.paper, pen: S.pen, hand: currentHand(), font: face,
        seed: S.seed, runs: resolved,
    }, extra || {});
}

function paintPreview() {
    const cv = $('hwPreview');
    if (!cv || !doc) return;
    /* Preview dpi follows the element's real width so the canvas is never
       larger than what is shown — a 300 dpi preview costs eight megapixels per
       keystroke and buys nothing. */
    const host = cv.parentElement;
    const cssW = Math.max(200, Math.min(520, (host ? host.clientWidth : 300) - 20));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const dpi = (cssW * dpr) / (doc.geom.pageW / 25.4);
    renderPage(doc, S.pageIndex, renderOpts({
        canvas: cv, dpi, scan: S.scan ? 0.9 : 0, transparent: S.alphaPreview,
    }));
    cv.style.width = cssW + 'px';
    cv.style.height = 'auto';
    const sheet = $('hwSheet');
    if (sheet) sheet.classList.toggle('is-alpha', !!S.alphaPreview);
    paintTrueSize();
}

/* ── the page at its real size ───────────────────────────────────────
 * The sheet above is an A4 in ~280 px: 33.6 dpi, where a 3.2 mm x-height is
 * 4.2 px. Every knob on this tool — pen pressure, wobble, slant, the pen
 * itself — changes the page by less than that, so at that size the preview can
 * show WHERE the text lands and nothing about what it looks like. Measured
 * 2026-09-12, together with a PDF whose pen was up to 9.8× the preview's and
 * which nobody could have caught by looking.
 *
 * So one strip of the page is drawn at true size instead. `clip` means only
 * that band is rasterised and only the lines inside it are inked, which is why
 * this costs less than the fitted sheet it sits under rather than more.
 */
function paintTrueSize() {
    const cv = $('hwTrue');
    if (!cv || !doc) return;
    const page = doc.pages[S.pageIndex];
    const line = page && page.lines.find(l => !l.blank && l.items && l.items.length);
    const host = cv.parentElement;
    const cssW = Math.max(180, Math.min(900, (host ? host.clientWidth : 280) - 18));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    /* 96 dpi is what "1 CSS pixel" means on the web, so a millimetre here is
       the millimetre the visitor's browser would print. */
    const dpi = 96 * dpr;
    const wMm = cssW / 96 * 25.4;
    /* Two whole lines, not one and a sliver: line spacing is a knob too, and a
       band cut through the middle of the next row reads as a broken render
       rather than as a window. */
    const pitch = Math.max(doc.pitch || 0, doc.em * 1.15);
    const hMm = pitch * 2;
    const baseline = line ? line.baseline : (doc.firstBaseline || doc.geom.margin.top);
    renderPage(doc, S.pageIndex, renderOpts({
        /* `scan: 0` on purpose. The scan pass tilts, vignettes and warms the
           WHOLE sheet; run on a 70 mm strip it would tilt the strip on its own
           axis and shade its corners — a lie about the page, dressed as detail.
           The sheet above shows the scan finish, this band shows the pen. */
        canvas: cv, dpi, scan: 0, transparent: S.alphaPreview,
        clip: { x: Math.max(0, doc.geom.margin.left - 3), y: Math.max(0, baseline - pitch * 0.78),
                w: wMm, h: hMm },
    }));
    cv.style.width = cssW + 'px';
    cv.style.height = 'auto';
    const box = $('hwTrueBox');
    if (box) box.classList.toggle('is-alpha', !!S.alphaPreview);
}

/* The ruling decides the size until the visitor overrides it; when it does, the
   slider must SHOW that size, or the only number on screen is a lie. */
function syncSizeReadout() {
    if (S.sizeTouched || !doc || !face) return;
    const xhMm = (xHeightOf(face) / face.unitsPerEm) * doc.em;
    S.sizeMm = Math.round(xhMm * 10) / 10;
    const el = $('hwSize');
    if (el && Math.abs(+el.value - S.sizeMm) > 0.05) {
        el.value = String(S.sizeMm);
        /* The shared fill styling only sees USER input, so a programmatic write
           leaves the blue where the old value was — thumb at 55%, fill at 80%
           (measured 2026-09-05). */
        if (window.__rangeFill) window.__rangeFill(el);
    }
    const out = $('hwSizeVal');
    if (out) out.textContent = S.sizeMm.toFixed(1) + ' ' + t('mm', 'mm');
}

function paintPager() {
    const info = $('hwPageInfo');
    if (info && doc) info.textContent = (S.pageIndex + 1) + ' / ' + doc.pages.length;
    // A one-page sheet has nothing to choose — the field appears with the second page.
    const pw = $('hwPagesWrap');
    if (pw && doc) pw.hidden = doc.pages.length < 2 && !(($('hwPages') || {}).value || '').trim();
    if ($('hwPrev')) $('hwPrev').disabled = S.pageIndex <= 0;
    if ($('hwNext')) $('hwNext').disabled = !doc || S.pageIndex >= doc.pages.length - 1;
}

function paintCount() {
    const el = $('hwCount');
    if (!el || !doc) return;
    el.textContent = fmt(t('count', ':chars chars · :pages pages'),
        { chars: textValue().length, pages: doc.pages.length });
}

function fmt(s, vars) {
    return String(s).replace(/:(\w+)/g, (m, k) => (vars[k] == null ? m : vars[k]));
}

/* ── selection ───────────────────────────────────────────────────── */
function readSelection() {
    const ta = $('hwText');
    if (!ta) return;
    const a = ta.selectionStart, b = ta.selectionEnd;
    lastSel = (b > a) ? { start: a, end: b } : null;
    paintScope();
}

function paintScope() {
    const bar = $('hwScope');
    if (!bar) return;
    const on = !!(lastSel && lastSel.end > lastSel.start);
    bar.hidden = !on;
    if (on) {
        const frag = textValue().slice(lastSel.start, lastSel.end);
        const short = frag.length > 34 ? frag.slice(0, 32) + '…' : frag;
        const label = $('hwScopeText');
        if (label) label.textContent = fmt(t('scope_selected', 'Changes apply to “:frag”'), { frag: short });
    }
}

function clearScope() {
    lastSel = null;
    paintScope();
    const ta = $('hwText');
    if (ta) { try { ta.setSelectionRange(ta.selectionEnd, ta.selectionEnd); } catch (e) {} }
}

function paintRunList() {
    const host = $('hwRuns');
    if (!host) return;
    host.textContent = '';
    const txt = textValue();
    if (!S.runs.length) { host.hidden = true; return; }
    host.hidden = false;
    S.runs.forEach((r, i) => {
        const chip = document.createElement('span');
        chip.className = 'hw-run';
        const frag = txt.slice(r.start, r.end);
        const name = document.createElement('button');
        name.type = 'button';
        name.className = 'hw-run-name';
        name.textContent = (frag.length > 18 ? frag.slice(0, 16) + '…' : frag) + ' · ' + describeRun(r);
        name.title = t('run_select', 'Select this fragment');
        name.addEventListener('click', () => {
            const ta = $('hwText');
            if (!ta) return;
            ta.focus();
            ta.setSelectionRange(r.start, r.end);
            readSelection();
        });
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'hw-run-del';
        del.textContent = '✕';
        del.setAttribute('aria-label', t('run_remove', 'Remove this styling'));
        del.addEventListener('click', () => {
            S.runs.splice(i, 1);
            paintRunList();
            schedule(true);
        });
        chip.append(name, del);
        host.appendChild(chip);
    });
}

function describeRun(r) {
    const bits = [];
    if (r.handId) bits.push(handLabel(handById(r.handId) || { face: 'caveat' }));
    if (r.sizeMm) bits.push(r.sizeMm.toFixed(1) + ' ' + t('mm', 'mm'));
    if (r.pen) bits.push(t('pen_' + (PENS[r.pen] || {}).label, r.pen));
    if (r.slant != null) bits.push((r.slant > 0 ? '+' : '') + r.slant + '°');
    if (r.press != null) bits.push(t('press_label_short', 'ink') + ' ' + Math.round(r.press * 100) + '%');
    if (r.jitter != null) bits.push(t('jitter_label_short', 'spread') + ' ' + Math.round(r.jitter * 100) + '%');
    return bits.join(' · ') || t('run_styled', 'styled');
}

/* ── hand picker ─────────────────────────────────────────────────── */
async function buildHandPicker() {
    const host = $('hwHands');
    if (!host) return;
    const text = textValue();
    let list = handsFor(text);
    if (customFace) {
        list = [Object.assign({}, HANDS[0], { id: '__mine', face: '__custom', mine: true })].concat(list);
    }
    if (!list.length) list = HANDS.slice(0, 6);
    const missing = unsupportedScripts(text);
    const note = $('hwScriptNote');
    if (note) {
        note.hidden = !missing.length;
        if (missing.length) {
            note.textContent = fmt(t('script_missing',
                'This text uses a writing system none of the hands covers (:s), so those characters will be blank.'),
                { s: missing.join(', ') });
        }
    }

    host.textContent = '';
    if (!list.some(h => h.id === S.handId)) S.handId = list[0].id;

    for (const h of list) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hw-hand';
        btn.setAttribute('aria-pressed', h.id === S.handId ? 'true' : 'false');
        btn.dataset.hand = h.id;
        const cv = document.createElement('canvas');
        cv.width = 272; cv.height = 80;
        const name = document.createElement('div');
        name.className = 'hw-hand-name';
        name.textContent = handLabel(h);
        btn.appendChild(cv); btn.appendChild(name);
        btn.addEventListener('click', () => {
            if (!(lastSel && lastSel.end > lastSel.start)) {
                window.__toolSettings && window.__toolSettings.save(CFG.slug, 'hand', h.id);
                [...host.children].forEach(c => c.setAttribute('aria-pressed', c.dataset.hand === h.id ? 'true' : 'false'));
            }
            applyChange({ handId: h.id });
            paintHandName();
            ae('hand_pick', { hand: h.id, scoped: lastSel ? 1 : 0 });
        });
        host.appendChild(btn);
        paintHandSample(cv, h).catch(() => {});
    }
    paintHandName();
}

function handLabel(h) {
    if (h.mine) return t('own_hand_name', 'My handwriting');
    const fam = (FACES[h.face] || {}).family || h.face;
    const mod = h.mod ? t('mod_' + h.mod, '') : '';
    return mod ? fam + ' · ' + mod : fam;
}

function paintHandName() {
    const el = $('hwHandName');
    const h = handById(S.handId);
    if (el) el.textContent = h ? handLabel(h) : '';
}

async function paintHandSample(cv, h) {
    const f = h.face === '__custom' ? customFace : await loadFace(h.face);
    if (!f) return;
    const hand = Object.assign({}, h);
    const run = layoutRun(t('hand_sample', 'Sample'), f, hand,
        { emMm: (4.4 / (xHeightOf(f) / f.unitsPerEm)), seed: 5 });
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    const k = cv.width / Math.max(28, run.width + 8);
    drawRun(ctx, run, f, hand, S.pen, 8, cv.height * 0.68, Math.min(k, 5.2));
}

/* ═══════════════════ export ═══════════════════ */

function baseName(ext) {
    const words = textValue().replace(/\s+/g, ' ').trim().split(' ').slice(0, 4).join(' ');
    let stem = words || t('file_stem', 'handwriting');
    if (window.__outputName && window.__outputName.sanitize) stem = window.__outputName.sanitize(stem);
    else stem = stem.replace(/[\\/:*?"<>|]+/g, '').slice(0, 60);
    return (stem || 'handwriting') + ext;
}

function busy(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.dataset.busy = on ? '1' : '';
}

async function exportPng(alpha, btn) {
    if (!doc) return;
    const sel = chosenPages();
    if (!sel) return;
    const run = beginPages(sel);
    if (window.__toolLimit && !run) return;
    busy(btn, true);
    try {
        const opts = renderOpts({ scan: (!alpha && S.scan) ? 0.9 : 0, transparent: !!alpha, dpi: 300 });
        const files = [];
        for (const i of sel) {
            const blob = await pngPage(doc, i, opts);
            files.push({ blob, name: baseName((doc.pages.length > 1 ? '-' + (i + 1) : '') + (alpha ? '-alpha' : '') + '.png') });
        }
        const res = await deliver(files, { zipName: baseName('.zip'), slug: CFG.slug });
        if (res && res.via !== 'refused' && res.via !== 'declined' && res.via !== 'empty') {
            run && run.commit();
            paintPagesCost();
            done('png', { alpha: alpha ? 1 : 0, pages: sel.length, of: doc.pages.length, via: res.via, runs: S.runs.length });
        } else {
            run && run.abort();
        }
    } catch (err) {
        run && run.abort();
        window.__handleError && window.__handleError(err, CFG.slug);
    } finally { busy(btn, false); }
}

async function exportPdf(btn) {
    if (!doc) return;
    const sel = chosenPages();
    if (!sel) return;
    const run = beginPages(sel);
    if (window.__toolLimit && !run) return;
    busy(btn, true);
    try {
        /* Raster only when the look actually depends on pixels — paper grain and
           the scan pass are not vector, everything else is sharper and ~50×
           smaller as paths. */
        const raster = !!S.scan;
        const blob = await pdfDocument(doc, renderOpts({
            scan: S.scan ? 0.9 : 0, raster, dpi: 220, title: baseName(''), pages: sel,
        }));
        const ok = await window.__downloadFile(blob, baseName('.pdf'));
        if (ok) { run && run.commit(); paintPagesCost(); done('pdf', { pages: sel.length, of: doc.pages.length, raster: raster ? 1 : 0, kb: Math.round(blob.size / 1024) }); }
        else run && run.abort();
    } catch (err) {
        run && run.abort();
        window.__handleError && window.__handleError(err, CFG.slug);
    } finally { busy(btn, false); }
}

function done(kind, extra) {
    if (drafts) drafts.exported();
    try {
        window.__aeToolComplete && window.__aeToolComplete(Object.assign({ f: 'export', kind }, extra || {}));
    } catch (e) {}
    /* A file handed to the browser is announced by the download door itself — where
       to find it, and NOT «saved» onto a disk too full to take it. Only a folder
       write has no other word. */
    if (extra && extra.via === 'folder') window.__toast && window.__toast(t('saved', 'Saved'), 'success');
}

function ae(name, data) {
    try { window.__aeFunnel && window.__aeFunnel(name, data || {}); } catch (e) {}
}

/* ═══════════════════ PDF lane ═══════════════════ */
const P = { handle: null, file: null, page: 0, fields: [], sizePt: 11, pen: 'blue_ball', view: null, dpr: 1 };

async function openPdfFile(file) {
    try {
        window.__toast && window.__toast(t('pdf_opening', 'Opening the document…'), 'info');
        P.handle = await PdfFill.openPdf(file);
        P.file = file;                 // its bytes go into the saved work
        P.page = 0; P.fields = [];
        $('hwPdfWork').hidden = false;
        $('hwPdfName').textContent = P.handle.name + ' · ' + fmt(t('pdf_pages', ':n pages'), { n: P.handle.pages.length });
        await paintPdfPage();
        ae('pdf_open', { pages: P.handle.pages.length });
        draftTouch(true);
    } catch (err) {
        window.__handleError && window.__handleError(err, CFG.slug);
    }
}

async function paintPdfPage() {
    if (!P.handle) return;
    P.dpr = Math.min(2, window.devicePixelRatio || 1);
    const host = $('hwPdfWrap');
    const wrapW = Math.min(820, (host.parentElement.clientWidth || 700) - 24);
    const { canvas, viewport } = await PdfFill.renderPdfPage(P.handle, P.page, wrapW, P.dpr);
    P.view = viewport;
    const target = $('hwPdfCanvas');
    target.width = canvas.width; target.height = canvas.height;
    target.style.width = (canvas.width / P.dpr) + 'px';
    target.style.height = (canvas.height / P.dpr) + 'px';
    target.getContext('2d').drawImage(canvas, 0, 0);
    const ov = $('hwPdfOverlay');
    ov.width = canvas.width; ov.height = canvas.height;
    ov.style.width = target.style.width; ov.style.height = target.style.height;
    $('hwPdfPageInfo').textContent = (P.page + 1) + ' / ' + P.handle.pages.length;
    $('hwPdfPrev').disabled = P.page <= 0;
    $('hwPdfNext').disabled = P.page >= P.handle.pages.length - 1;
    paintPdfOverlay();
}

function paintPdfOverlay() {
    const ov = $('hwPdfOverlay');
    if (!ov || !P.view) return;
    const ctx = ov.getContext('2d');
    ctx.setTransform(P.dpr, 0, 0, P.dpr, 0, 0);
    ctx.clearRect(0, 0, ov.width, ov.height);
    const hand = currentHand();
    for (const f of P.fields) {
        if (f.page !== P.page) continue;
        if (!f.text) {
            /* An empty field is invisible ink; show where it will go. */
            const px = PdfFill.fieldToScreen(P.view, f.xPt, f.yPt, P.dpr);
            ctx.save();
            ctx.strokeStyle = 'rgba(56,189,248,.85)';
            ctx.setLineDash([5, 4]);
            ctx.lineWidth = 1.4;
            ctx.beginPath();
            ctx.moveTo(px.x, px.y + 1); ctx.lineTo(px.x + 90, px.y + 1);
            ctx.stroke();
            ctx.restore();
            continue;
        }
        try { PdfFill.drawFieldPreview(ctx, f, face, hand, P.view, P.dpr); } catch (e) {}
    }
}

function addField(xPt, yPt) {
    P.fields.push({ page: P.page, xPt, yPt, text: '', sizePt: P.sizePt, pen: P.pen, seed: (P.fields.length + 3) * 17 });
    paintPdfFields();
    paintPdfOverlay();
    draftTouch(true);
}

function paintPdfFields() {
    const host = $('hwPdfFields');
    const empty = $('hwPdfEmpty');
    if (!host) return;
    host.textContent = '';
    const mine = P.fields.map((f, i) => [f, i]).filter(([f]) => f.page === P.page);
    if (empty) empty.hidden = mine.length > 0;
    for (const [f, i] of mine) {
        const row = document.createElement('div');
        row.className = 'hw-fieldrow';
        const idx = document.createElement('span');
        idx.className = 'hw-idx'; idx.textContent = String(i + 1);
        const inp = document.createElement('input');
        inp.type = 'text'; inp.value = f.text;
        inp.placeholder = t('pdf_field_placeholder', 'What to write here');
        inp.addEventListener('input', () => { f.text = inp.value; paintPdfOverlay(); draftTouch(true); });
        const size = document.createElement('input');
        size.type = 'number'; size.min = '6'; size.max = '28'; size.step = '0.5'; size.value = f.sizePt;
        size.addEventListener('input', () => { f.sizePt = +size.value || 11; paintPdfOverlay(); draftTouch(false); });
        const del = document.createElement('button');
        del.type = 'button'; del.className = 'hw-del'; del.textContent = '✕';
        del.setAttribute('aria-label', t('pdf_field_remove', 'Remove'));
        del.addEventListener('click', () => {
            P.fields.splice(P.fields.indexOf(f), 1);
            paintPdfFields(); paintPdfOverlay();
            draftTouch(true);
        });
        row.append(idx, inp, size, del);
        host.appendChild(row);
    }
}

async function savePdf(btn) {
    if (!P.handle) return;
    const filled = P.fields.filter(f => f.text && f.text.trim());
    if (!filled.length) {
        window.__toast && window.__toast(t('pdf_nothing', 'Nothing is written in yet — click on the document and type.'), 'warning');
        return;
    }
    /* A page of the form that carries handwriting is a page of the day's pages —
       keyed by what is written on it, so moving a line or changing the pen costs
       nothing again, and a 30-page contract signed on one page is one page. */
    const byPage = new Map();
    for (const f of filled) { if (!byPage.has(f.page)) byPage.set(f.page, []); byPage.get(f.page).push(f.text.trim()); }
    const keys = Array.from(byPage, ([pg, texts]) => 'pf:' + hashStr((P.handle.name || '') + '|' + pg + '|' + texts.join('\n')));
    const run = window.__toolLimit && window.__toolLimit.begin(CFG.slug, keys);
    if (window.__toolLimit && !run) return;
    busy(btn, true);
    try {
        await ensureFaces();
        const hand = currentHand();
        const blob = await PdfFill.fillAndExport(P.handle, filled, () => ({ font: face, hand }));
        const name = (P.handle.name || 'form').replace(/\.pdf$/i, '') + t('pdf_suffix', '-filled') + '.pdf';
        const ok = await window.__downloadFile(blob, name);
        if (ok) { run && run.commit(); done('pdf_fill', { fields: filled.length, pages: byPage.size, of: P.handle.pages.length }); }
        else run && run.abort();
    } catch (err) {
        run && run.abort();
        window.__handleError && window.__handleError(err, CFG.slug);
    } finally { busy(btn, false); }
}

/* ═══════════════════ own hand ═══════════════════ */
const O = { chars: [], idx: 0, glyphs: {}, strokes: [], drawing: false, cur: null };

function ownInit() {
    O.chars = Own.charsFor(CFG.locale || 'en');
    const saved = loadOwn();
    if (saved) O.glyphs = saved;
    paintCaps();
    paintTarget();
    paintOwnCount();
}

function paintTarget() {
    const el = $('hwDrawTarget');
    if (el) el.textContent = O.chars[O.idx] || '';
    const bar = $('hwDrawBar');
    const total = O.chars.length || 1;
    if (bar) bar.style.width = Math.round(Object.keys(O.glyphs).length / total * 100) + '%';
    const prog = $('hwDrawProg');
    if (prog) prog.textContent = fmt(t('own_progress', ':done of :total letters'),
        { done: Object.keys(O.glyphs).length, total });
    clearCell();
}

function paintCaps() {
    const host = $('hwDrawCaps');
    if (!host) return;
    host.textContent = '';
    O.chars.forEach((ch, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hw-cap' + (O.glyphs[ch] ? ' done' : '') + (i === O.idx ? ' cur' : '');
        b.textContent = ch;
        b.addEventListener('click', () => { O.idx = i; paintCaps(); paintTarget(); });
        host.appendChild(b);
    });
}

function cellCtx() {
    const cv = $('hwDrawCell');
    return cv ? cv.getContext('2d') : null;
}

function clearCell() {
    const cv = $('hwDrawCell');
    const ctx = cellCtx();
    if (!cv || !ctx) return;
    O.strokes = [];
    paintCellBackground(cv, ctx);
}

function paintCellBackground(cv, ctx) {
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cv.width, cv.height);
    /* Guides in the same proportions the capture maths assumes, so a letter
       written to them needs no rescaling and sits on the line later. */
    const bl = cv.height * (1 - Own.CELL.baseline / Own.CELL.h);
    const xh = bl - cv.height * (Own.CELL.xHeight / Own.CELL.h);
    ctx.strokeStyle = '#f0c3c9'; ctx.lineWidth = 1;
    line(ctx, 0, bl, cv.width, bl);
    ctx.strokeStyle = '#dfe7ef';
    line(ctx, 0, xh, cv.width, xh);
    ctx.strokeStyle = '#eef2f6';
    const cap = cv.height * (1 - Own.CELL.cap / Own.CELL.h);
    line(ctx, 0, cap, cv.width, cap);
}

function line(ctx, x1, y1, x2, y2) { ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke(); }

function repaintCell() {
    const cv = $('hwDrawCell'), ctx = cellCtx();
    if (!cv || !ctx) return;
    paintCellBackground(cv, ctx);
    ctx.strokeStyle = '#1a348c';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.lineWidth = 6;
    for (const s of O.strokes) {
        if (s.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(s[0].x, s[0].y);
        for (let i = 1; i < s.length; i++) ctx.lineTo(s[i].x, s[i].y);
        ctx.stroke();
    }
}

function bindDrawing() {
    const cv = $('hwDrawCell');
    if (!cv) return;
    const pt = (e) => {
        const r = cv.getBoundingClientRect();
        return { x: (e.clientX - r.left) * (cv.width / r.width),
                 y: (e.clientY - r.top) * (cv.height / r.height), p: e.pressure || 0.5 };
    };
    cv.addEventListener('pointerdown', (e) => {
        cv.setPointerCapture(e.pointerId);
        O.drawing = true; O.cur = [pt(e)]; O.strokes.push(O.cur);
        e.preventDefault();
    });
    cv.addEventListener('pointermove', (e) => {
        if (!O.drawing) return;
        O.cur.push(pt(e));
        repaintCell();
    });
    const end = () => { O.drawing = false; O.cur = null; };
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);
    cv.addEventListener('pointerleave', end);
}

async function keepDrawn() {
    const cv = $('hwDrawCell');
    if (!cv || !O.strokes.length) {
        window.__toast && window.__toast(t('own_empty', 'Draw the letter first.'), 'warning');
        return;
    }
    const g = await Own.strokesToGlyph(O.strokes, cv.width, {});
    if (!g) { window.__toast && window.__toast(t('own_empty', 'Draw the letter first.'), 'warning'); return; }
    O.glyphs[O.chars[O.idx]] = g;
    saveOwn();
    O.idx = Math.min(O.chars.length - 1, O.idx + 1);
    paintCaps(); paintTarget(); paintOwnCount(); drawOwnPreview();
    ae('own_letter', { n: Object.keys(O.glyphs).length });
}

function paintOwnCount() {
    const el = $('hwOwnCount');
    if (el) el.textContent = fmt(t('own_captured', ':n letters captured'), { n: Object.keys(O.glyphs).length });
}

function buildCustomFace() {
    if (!Object.keys(O.glyphs).length) return null;
    customFace = Own.makeCustomFace(O.glyphs, { base: face, name: t('own_hand_name', 'My handwriting') });
    registerCustomFace('__custom', customFace, { family: t('own_hand_name', 'My handwriting'), scripts: ['latin', 'cyrillic'] });
    if (!HANDS.some(h => h.id === '__mine')) {
        HANDS.unshift(Object.assign({}, HANDS[0], { id: '__mine', face: '__custom', mine: true }));
    }
    return customFace;
}

function drawOwnPreview() {
    const cv = $('hwOwnPreview');
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cv.width, cv.height);
    const f = buildCustomFace();
    if (!f) {
        ctx.fillStyle = '#9aa4af'; ctx.font = '15px system-ui'; ctx.textAlign = 'center';
        ctx.fillText(t('own_preview_empty', 'Your letters will appear here'), cv.width / 2, cv.height / 2);
        ctx.textAlign = 'start';
        return;
    }
    const hand = Object.assign({}, HANDS[0], { id: '__mine', face: '__custom' });
    const run = layoutRun(t('own_preview_text', 'The quick brown fox'), f, hand, { emMm: 8, seed: 3 });
    const k = Math.min(6, (cv.width - 30) / Math.max(20, run.width));
    drawRun(ctx, run, f, hand, S.pen, 15, cv.height * 0.72, k);
}

function saveOwn() {
    try {
        const flat = {};
        for (const k in O.glyphs) flat[k] = { c: O.glyphs[k].commands, a: O.glyphs[k].advance };
        localStorage.setItem('hw_own_hand', JSON.stringify(flat));
    } catch (e) { /* private window, or storage off — the hand simply does not persist */ }
}

function loadOwn() {
    try {
        const raw = localStorage.getItem('hw_own_hand');
        if (!raw) return null;
        const flat = JSON.parse(raw);
        const out = {};
        for (const k in flat) out[k] = { commands: flat[k].c, advance: flat[k].a };
        return out;
    } catch (e) { return null; }
}

function printSheet() {
    const svg = Own.sheetSvg(O.chars, { title: t('own_sheet_title', 'Write each letter inside its box') });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) { window.__downloadFile(blob, 'handwriting-sheet.svg'); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    ae('own_sheet_print', {});
}

async function readSheetPhoto(file) {
    const status = $('hwPhotoStatus');
    const say = (msg) => { if (status) status.textContent = msg; };
    say(t('own_photo_reading', 'Reading the photo…'));
    try {
        const img = await loadImage(file);
        const maxSide = 1800;
        const sc = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * sc), h = Math.round(img.height * sc);
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const corners = Own.findSheetCorners(ctx.getImageData(0, 0, w, h));
        if (!corners) { say(t('own_photo_nomarkers', '')); return; }
        const H = Own.sheetHomography(corners);
        if (!H) { say(t('own_photo_nomarkers', '')); return; }

        /* Sheet geometry must match sheetSvg() exactly. */
        const M = 14, marker = 8, cols = 8, W = 210, HH = 297;
        const cellW = (W - M * 2) / cols;
        const top = M + marker + 8;
        const u = (mmX) => (mmX - (M + marker / 2)) / ((W - M - marker / 2) - (M + marker / 2));
        const v = (mmY) => (mmY - (M + marker / 2)) / ((HH - M - marker / 2) - (M + marker / 2));

        const CELL = 128;
        const cell = document.createElement('canvas');
        cell.width = CELL; cell.height = CELL;
        const cctx = cell.getContext('2d', { willReadFrequently: true });
        const src = ctx.getImageData(0, 0, w, h);
        let got = 0;
        for (let i = 0; i < O.chars.length; i++) {
            const r = Math.floor(i / cols), c = i % cols;
            const x0 = M + c * cellW, y0 = top + r * cellW;
            if (y0 + cellW > HH - M - marker - 4) break;
            const dst = cctx.createImageData(CELL, CELL);
            for (let py = 0; py < CELL; py++) {
                for (let px = 0; px < CELL; px++) {
                    const mmX = x0 + (px / CELL) * cellW;
                    const mmY = y0 + (py / CELL) * cellW;
                    const [ix, iy] = Own.applyH(H, u(mmX), v(mmY));
                    const sx = Math.round(ix), sy = Math.round(iy);
                    const si = (sy * w + sx) * 4, di = (py * CELL + px) * 4;
                    if (sx < 0 || sy < 0 || sx >= w || sy >= h) {
                        dst.data[di] = dst.data[di + 1] = dst.data[di + 2] = 255; dst.data[di + 3] = 255; continue;
                    }
                    dst.data[di] = src.data[si]; dst.data[di + 1] = src.data[si + 1];
                    dst.data[di + 2] = src.data[si + 2]; dst.data[di + 3] = 255;
                }
            }
            cctx.putImageData(dst, 0, 0);
            const mask = Own.binariseCell(cctx, CELL);
            if (!mask) continue;
            const g = Own.traceGlyph(mask, CELL, CELL, CELL);
            if (g) { O.glyphs[O.chars[i]] = g; got++; }
        }
        saveOwn();
        paintCaps(); paintTarget(); paintOwnCount(); drawOwnPreview();
        say(got ? fmt(t('own_photo_ok', 'Read :n letters from the sheet.'), { n: got })
                : t('own_photo_none', 'No letters were found in the boxes.'));
        ae('own_photo', { got });
    } catch (err) {
        say(t('own_photo_fail', 'Could not read the photo.'));
        window.__handleError && window.__handleError(err, CFG.slug);
    }
}

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const im = new Image();
        im.onload = () => { URL.revokeObjectURL(url); resolve(im); };
        im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
        im.src = url;
    });
}

/* ═══════════════════ import text ═══════════════════
   The text of the file comes from the shared door, public/js/document-text.js. The
   reader that lived here loaded mammoth with import(): a UMD script imports as
   an EMPTY module, which won over window.mammoth — every .docx from 04.09 to
   28.10 ended in «something broke on our side» (942 sessions, zero imports).
   It also pasted .rtf as raw markup, a cp1251/KOI8/cp866 .txt as U+FFFD replacement marks, lost
   the line breaks of a PDF and of a .docx, and a scanned PDF replaced the
   person's text with nothing. Each refusal now names its reason; the text in
   the field stays as it was. */
const IMPORT_MAX_PDF_PAGES = 40;
const IMPORT_SAY = {
    locked: () => t('import_locked', 'The document is password-protected. Open it, save a copy without the password and upload that copy.'),
    scanned: () => t('import_scanned', 'This PDF has no text in it — the pages are pictures (a scan or photos). Recognise the text first, then paste it here.'),
    empty: () => t('import_empty', 'There is no text in this file.'),
    not_text: () => t('import_not_text', 'This is not a text file. Upload .docx, .doc, .pdf, .rtf, .odt or .txt.'),
    unsupported: () => t('import_unsupported', 'This is a spreadsheet or a presentation, not a text document. Copy the text you need and paste it into the field.'),
    unreadable: () => t('import_unreadable', 'The file could not be read: it is damaged or is not the format its name says. Open it, save it again and upload the new copy.'),
    reader_unavailable: () => t('import_reader_unavailable', 'The document reader did not load. Check the connection and try again.'),
};

async function importTextFile(file) {
    const ext = ((/\.([a-z0-9]{1,8})$/i.exec((file && file.name) || '') || [])[1] || '').toLowerCase();
    /* The reader for .docx is 636 KB fetched on first use — 1–3 s of silence on
       the 4G link of the report. Say so, but only when it is that slow. */
    let note = null;
    const slow = setTimeout(() => { note = window.__toast && window.__toast(t('import_reading', 'Reading the file…'), 'info'); }, 700);
    try {
        const r = await docText(file, { bullet: '–', maxPages: IMPORT_MAX_PDF_PAGES, pdfjs: PdfFill.loadPdfJs });
        clearTimeout(slow);
        if (note && note.dismiss) note.dismiss();
        setText(r.text);
        if (r.pagesRead < r.pages) {
            window.__toast && window.__toast(fmt(t('import_pages_cut', 'The first :n pages of :total were taken.'), { n: r.pagesRead, total: r.pages }), 'info');
        }
        ae('import', { kind: r.kind, ext, enc: r.encoding || '', chars: r.text.length, pages: r.pages || 0 });
    } catch (err) {
        clearTimeout(slow);
        if (note && note.dismiss) note.dismiss();
        const say = err && IMPORT_SAY[err.code];
        if (say) {
            window.__toast && window.__toast(say(), 'warning');
            ae('import_refused', { code: err.code, ext, why: String((err.cause && err.cause.message) || '').slice(0, 120) });
            return;
        }
        window.__handleError && window.__handleError(err, 'hw_import', { say: t('import_fail', 'Could not read the file.') });
    }
}

function setText(v) {
    const ta = $('hwText');
    if (!ta) return;
    ta.value = v;
    taPrev = v;
    S.runs = [];                       // the ranges belonged to the old text
    lastSel = null;
    if ($('hwPages')) $('hwPages').value = '';   // so was the page choice
    paintScope(); paintRunList();
    buildHandPicker();
    schedule(true);
    draftTouch(true);
}

/* ═══════════════════ the work survives a crash, a closed tab and F5 ═══════════════════
   Audit of 30.09.2026: the settings here were kept, the WORK was not — the text, its
   styled pieces, an opened PDF and what was written into it lived in memory only. RU,
   30 days: 285 people worked on a text, left without a sheet and came back within
   three days to the sample text. The shared «my work» controller
   (public/js/tool-draft-editor.js over tool-draft.js) keeps it; this part says what
   the work IS, how it comes back and what a new one looks like. */
let drafts = null;
let previewCache = { url: '', at: 0 };

function draftTouch(counted) { if (drafts) drafts.touch(counted); }

function currentTab() {
    const i = TABS.findIndex(([tabId]) => { const b = $(tabId); return b && b.getAttribute('aria-selected') === 'true'; });
    return i < 0 ? 0 : i;
}

/* A small picture of the sheet for the list, from the preview already on screen. */
function sheetPreview() {
    const now = Date.now();
    if (previewCache.url && now - previewCache.at < 1500) return previewCache.url;
    try {
        const src = $('hwPreview');
        if (!src || !src.width || !src.height) return previewCache.url;
        const k = 120 / Math.max(src.width, src.height);
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(src.width * k)); c.height = Math.max(1, Math.round(src.height * k));
        const cx = c.getContext('2d');
        cx.fillStyle = '#fff'; cx.fillRect(0, 0, c.width, c.height);
        cx.drawImage(src, 0, 0, c.width, c.height);
        previewCache = { url: c.toDataURL('image/jpeg', 0.7), at: now };
    } catch (e) { /* a row without a picture still opens the work */ }
    return previewCache.url;
}

function draftPayload() {
    const text = textValue();
    const pdf = P.handle && P.file ? { page: P.page, fields: P.fields, sizePt: P.sizePt, pen: P.pen } : null;
    const first = (text.split(/\r?\n/).find(s => s.trim()) || '').trim();
    return {
        state: { text, runs: S.runs, seed: S.seed, tab: currentTab(), pdf },
        files: pdf ? [P.file] : [],
        label: (first || (pdf ? (P.handle.name || '') : '')).slice(0, 80),
        preview: sheetPreview(),
        meta: { chars: text.length, pdf: pdf ? 1 : 0 },
    };
}

/* Put a saved work back. → the names of files that did not come back. */
async function applyDraft(d) {
    const st = d.state || {};
    const ta = $('hwText');
    if (ta) { ta.value = typeof st.text === 'string' ? st.text : ''; taPrev = ta.value; }
    S.runs = Array.isArray(st.runs) ? st.runs.filter(r => r && r.end > r.start) : [];
    if (st.seed) S.seed = st.seed;
    lastSel = null;
    const missing = [];
    if (st.pdf) {
        const f = (d.files || [])[0];
        if (f) await openPdfFile(f);
        if (f && P.handle) {
            P.fields = Array.isArray(st.pdf.fields) ? st.pdf.fields : [];
            if (st.pdf.sizePt) P.sizePt = st.pdf.sizePt;
            if (st.pdf.pen) P.pen = st.pdf.pen;
            P.page = Math.min(Math.max(0, st.pdf.page || 0), P.handle.pages.length - 1);
            await paintPdfPage();
            paintPdfFields();
        } else {
            missing.push('pdf');
        }
    }
    showTab(typeof st.tab === 'number' ? st.tab : 0);
    paintScope(); paintRunList();
    await buildHandPicker();
    schedule(true);
    return missing;
}

/* A new work: the sample text, no styled pieces, no document. */
function draftFresh() {
    const ta = $('hwText');
    if (ta) { ta.value = ta.defaultValue; taPrev = ta.value; }
    S.runs = []; lastSel = null;
    P.handle = null; P.file = null; P.fields = []; P.page = 0; P.view = null;
    const w = $('hwPdfWork'); if (w) w.hidden = true;
    paintPdfFields();
    try { if (window.ToolUploader) window.ToolUploader.clear('hwPdfFile'); } catch (e) {}
    showTab(0);
    paintScope(); paintRunList();
    buildHandPicker().then(() => schedule(true));
}

function draftsBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('hwDrafts')) return;
    drafts = window.__toolDraft.editor({
        slug: CFG.slug, root: 'hwDrafts', icon: '✍️',
        payload: draftPayload, apply: applyDraft, fresh: draftFresh,
        started: () => textValue() !== ($('hwText') || {}).defaultValue || S.runs.length > 0 || !!P.handle,
    });
    drafts.boot();
}

/* ═══════════════════ wiring ═══════════════════ */

/* A slider that reports while it is dragged. `layout` says whether the value
   moves letters (re-layout) or only inks them (redraw). */
function slider(id, key, opts) {
    const el = $(id);
    if (!el) return;
    opts = opts || {};
    const toVal = opts.map || ((v) => v);
    const paint = () => {
        const out = $(id + 'Val');
        if (out && opts.format) out.textContent = opts.format(+el.value);
    };
    bindSetting(el, opts.settingKey || key, el.value);
    el.addEventListener('input', () => {
        if (opts.before) opts.before();
        applyChange({ [key]: toVal(+el.value) });
        paint();
    });
    /* Reflect the CURRENT value at boot without touching runs. */
    S[key] = toVal(+el.value);
    paint();
}

function fillSelect(id, entries, current) {
    const el = $(id);
    if (!el) return;
    el.textContent = '';
    for (const [value, label] of entries) {
        const o = document.createElement('option');
        o.value = value; o.textContent = label;
        el.appendChild(o);
    }
    el.value = current;
}

function buildPens(hostId, onPick, current) {
    const host = $(hostId);
    if (!host) return;
    host.textContent = '';
    for (const [id, p] of Object.entries(PENS)) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hw-sw';
        b.style.background = `rgb(${p.rgb.join(',')})`;
        b.title = t('pen_' + p.label, p.label);
        b.setAttribute('aria-label', t('pen_' + p.label, p.label));
        b.setAttribute('aria-pressed', id === current ? 'true' : 'false');
        b.addEventListener('click', () => {
            [...host.children].forEach(c => c.setAttribute('aria-pressed', 'false'));
            b.setAttribute('aria-pressed', 'true');
            onPick(id);
        });
        host.appendChild(b);
    }
}

function boot() {
    TABS.forEach(([tabId], i) => { const b = $(tabId); b && b.addEventListener('click', () => showTab(i)); });

    const ta = $('hwText');
    if (ta) {
        taPrev = ta.value;
        let prevSelStart = 0;
        const noteSel = () => { prevSelStart = ta.selectionStart; };
        ['keydown', 'mousedown'].forEach(ev => ta.addEventListener(ev, noteSel));
        ta.addEventListener('input', () => {
            /* Remap the styled ranges around the edit. The edit point is where
               the two strings stop matching — cheaper and more reliable than
               trusting selectionStart across paste, undo and IME. */
            const now = ta.value;
            const prev = taPrev;
            let a = 0;
            const min = Math.min(prev.length, now.length);
            while (a < min && prev[a] === now[a]) a++;
            let z = 0;
            while (z < min - a && prev[prev.length - 1 - z] === now[now.length - 1 - z]) z++;
            remapRuns(a, prev.length - a - z, now.length - a - z);
            taPrev = now;
            paintRunList();
            schedule(true);
            draftTouch(true);
        });
        ['select', 'keyup', 'mouseup', 'focus'].forEach(ev => ta.addEventListener(ev, readSelection));
        ta.addEventListener('scroll', () => {});
        let scriptSig = [...scriptsIn(ta.value)].sort().join();
        ta.addEventListener('input', () => {
            const sig = [...scriptsIn(ta.value)].sort().join();
            if (sig !== scriptSig) { scriptSig = sig; buildHandPicker(); }
        });
    }
    on('hwScopeClear', 'click', clearScope);

    fillSelect('hwPaper', Object.keys(PAPERS).filter(k => k !== 'none')
        .map(k => [k, t('paper_' + PAPERS[k].label, PAPERS[k].label)]), S.paper);
    fillSelect('hwPageSize', Object.entries(PAGES).map(([k, v]) => [k, v.label]), S.page);

    on('hwPaper', 'change', (e) => { S.paper = e.target.value; syncLineControl(); schedule(true); });
    on('hwPageSize', 'change', (e) => { S.page = e.target.value; S.pageIndex = 0; schedule(true); });
    on('hwMargin', 'change', (e) => { S.margins = e.target.value; schedule(true); });
    bindSetting($('hwPaper'), 'paper', S.paper);
    bindSetting($('hwPageSize'), 'page', S.page);
    bindSetting($('hwMargin'), 'margins', S.margins);

    slider('hwSize', 'sizeMm', { format: v => v.toFixed(1) + ' ' + t('mm', 'mm'),
                                 before: () => { S.sizeTouched = true; } });
    slider('hwSlant', 'slant', { format: v => (v > 0 ? '+' : '') + v + '°' });
    slider('hwJitter', 'jitter', { map: v => v / 100, format: v => v + '%' });
    slider('hwWobble', 'wobble', { map: v => v / 100, format: v => v + '%' });
    slider('hwPress', 'press', { map: v => v / 100, format: v => v + '%' });
    slider('hwTight', 'tight', { map: v => v / 1000, format: v => (v > 0 ? '+' : '') + (v / 10).toFixed(1) + '%' });
    slider('hwLine', 'lineHeight', { map: v => v / 100, format: v => (v / 100).toFixed(2) + '×' });

    ['hwRuled', 'hwScan', 'hwLandscape', 'hwAlphaPreview'].forEach(id => {
        const el = $(id);
        if (!el) return;
        bindSetting(el, id, el.checked);
        el.addEventListener('change', () => {
            S.followRuling = $('hwRuled') ? $('hwRuled').checked : true;
            syncLineControl();
            S.scan = $('hwScan') ? $('hwScan').checked : false;
            S.landscape = $('hwLandscape') ? $('hwLandscape').checked : false;
            S.alphaPreview = $('hwAlphaPreview') ? $('hwAlphaPreview').checked : false;
            S.pageIndex = 0;
            schedule(true);
        });
    });
    S.followRuling = $('hwRuled') ? $('hwRuled').checked : true;
    syncLineControl();

    buildPens('hwPens', id => { applyChange({ pen: id }); buildHandPicker(); }, S.pen);
    buildPens('hwPdfPens', id => { P.pen = id; P.fields.forEach(f => { f.pen = id; }); paintPdfOverlay(); draftTouch(false); }, P.pen);

    const savedHand = window.__toolSettings && window.__toolSettings.load(CFG.slug, 'hand', null);
    if (savedHand && (handById(savedHand) || savedHand === '__mine')) S.handId = savedHand;

    on('hwPrev', 'click', () => { S.pageIndex = Math.max(0, S.pageIndex - 1); paintPreview(); paintPager(); });
    on('hwNext', 'click', () => { if (doc) S.pageIndex = Math.min(doc.pages.length - 1, S.pageIndex + 1); paintPreview(); paintPager(); });
    on('hwDice', 'click', () => { S.seed = (S.seed * 1103515245 + 12345) >>> 8 || 1; schedule(true); ae('reroll', {}); draftTouch(false); });

    on('hwPng', 'click', (e) => exportPng(false, e.currentTarget));
    on('hwPngAlpha', 'click', (e) => exportPng(true, e.currentTarget));
    on('hwPdf', 'click', (e) => exportPdf(e.currentTarget));
    on('hwPdfSave', 'click', (e) => savePdf(e.currentTarget));
    on('hwPages', 'input', () => paintPagesCost());

    /* Carry the platform's own formats/size sentence into the button's tooltip —
       hidden visually in a toolbar, but it must stay reachable.
       ⚠️ Read it AT HOVER, not at boot: the line is composed by the shared
       uploader at runtime (the size half of it depends on deviceMemory), so at
       boot the node exists and is still empty — the first version of this ran,
       found '', and silently set nothing. */
    (() => {
        const up = $('hwFile');
        if (!up) return;
        const carry = () => {
            const fm = up.querySelector('.tu-formats');
            const txt = fm ? fm.textContent.trim() : '';
            if (txt && up.title !== txt) up.title = txt;
        };
        ['pointerenter', 'focusin', 'touchstart'].forEach(ev => up.addEventListener(ev, carry, { passive: true }));
        carry();
    })();
    uploader('hwFile', importTextFile);
    uploader('hwPdfFile', openPdfFile);
    uploader('hwSheetPhoto', readSheetPhoto);

    on('hwPdfPrev', 'click', async () => { P.page = Math.max(0, P.page - 1); await paintPdfPage(); paintPdfFields(); });
    on('hwPdfNext', 'click', async () => { if (P.handle) P.page = Math.min(P.handle.pages.length - 1, P.page + 1); await paintPdfPage(); paintPdfFields(); });
    on('hwPdfClear', 'click', () => { P.fields = P.fields.filter(f => f.page !== P.page); paintPdfFields(); paintPdfOverlay(); draftTouch(true); });
    on('hwPdfBlanks', 'click', async () => {
        if (!P.handle) return;
        const found = await PdfFill.findBlanks(P.handle, P.page, {});
        if (!found.length) {
            window.__toast && window.__toast(t('pdf_no_blanks', 'No blank lines were recognised on this page — click where you want to write.'), 'info');
            return;
        }
        for (const b of found) P.fields.push({ page: P.page, xPt: b.xPt, yPt: b.yPt, text: '', sizePt: P.sizePt, pen: P.pen, seed: (P.fields.length + 3) * 17 });
        paintPdfFields(); paintPdfOverlay();
        ae('pdf_blanks', { n: found.length });
        draftTouch(true);
    });
    const ov = $('hwPdfOverlay');
    if (ov) ov.addEventListener('click', (e) => {
        if (!P.view) return;
        const r = ov.getBoundingClientRect();
        const f = PdfFill.screenToField(P.view, e.clientX - r.left, e.clientY - r.top, P.dpr);
        addField(f.xPt, f.yPt);
    });
    const ps = $('hwPdfSize');
    if (ps) {
        const paint = () => { const o = $('hwPdfSizeVal'); if (o) o.textContent = ps.value + ' pt'; };
        ps.addEventListener('input', () => {
            P.sizePt = +ps.value;
            P.fields.filter(f => f.page === P.page).forEach(f => { f.sizePt = P.sizePt; });
            paint(); paintPdfFields(); paintPdfOverlay();
            draftTouch(false);
        });
        paint();
    }

    onEl('hwPdfFile', 'tool-uploader:clear', () => {
        /* × on the zone means "different document": the fields belonged to the
           old one and would be placed at meaningless coordinates in the next. */
        P.handle = null; P.file = null; P.fields = []; P.page = 0; P.view = null;
        const w = $('hwPdfWork'); if (w) w.hidden = true;
        paintPdfFields();
        draftTouch(true);
    });

    ownInit();
    bindDrawing();
    on('hwDrawUndo', 'click', () => { O.strokes.pop(); repaintCell(); });
    on('hwDrawSkip', 'click', () => { O.idx = Math.min(O.chars.length - 1, O.idx + 1); paintCaps(); paintTarget(); });
    on('hwDrawNext', 'click', keepDrawn);
    on('hwSheetPrint', 'click', printSheet);
    on('hwOwnReset', 'click', () => {
        O.glyphs = {}; customFace = null;
        try { localStorage.removeItem('hw_own_hand'); } catch (e) {}
        const i = HANDS.findIndex(h => h.id === '__mine');
        if (i >= 0) HANDS.splice(i, 1);
        if (S.handId === '__mine') S.handId = 'caveat';
        paintCaps(); paintTarget(); paintOwnCount(); drawOwnPreview(); buildHandPicker(); schedule(true);
    });
    on('hwOwnUse', 'click', () => {
        if (!buildCustomFace()) { window.__toast && window.__toast(t('own_need_letters', 'Write a few letters first.'), 'warning'); return; }
        S.handId = '__mine';
        showTab(0);
        buildHandPicker();
        schedule(true);
    });

    let resizeT = 0;
    window.addEventListener('resize', () => { clearTimeout(resizeT); resizeT = setTimeout(paintPreview, 200); });

    paintRunList();
    paintScope();
    buildHandPicker().then(() => schedule(true));
    try { window.__stage && window.__stage('ready'); } catch (e) {}
    draftsBoot();
}

/* On ruled paper the ruling IS the line spacing, so the control has nothing to
   say. Greyed out with the reason, rather than left live and inert. */
function syncLineControl() {
    const el = $('hwLine');
    if (!el) return;
    const ruled = !!(S.followRuling && rulingOf(S.paper, true));
    el.disabled = ruled;
    const field = el.closest('.hw-field');
    if (field) field.classList.toggle('is-off', ruled);
    const out = $('hwLineVal');
    if (out && ruled) out.textContent = t('line_from_ruling', 'from the ruling');
}

function on(id, ev, fn) { const el = $(id); if (el) el.addEventListener(ev, fn); }
const onEl = on;

/* window.__bindUploaderFile, not a raw addEventListener: this module is loaded
   as a deferred ES module, so on a slow connection the visitor can pick a file
   BEFORE it runs — the event then goes to nobody and the tool sits dead until
   they pick again (the /vocal-remover report of 2026-05-08). The shared binder
   replays el.__pendingFile, de-dupes the replay against the live event and
   expires the stamp. UploaderListenerRaceTest holds this. */
function uploader(id, fn) {
    const el = $(id);
    if (!el) return;
    if (typeof window.__bindUploaderFile === 'function') {
        window.__bindUploaderFile(el, (file) => fn(file));
        return;
    }
    el.addEventListener('tool-uploader:file', (e) => {
        const f = e.detail && e.detail.file;
        if (f) fn(f);
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
